import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { buyerInsightsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp,
  ShoppingBag,
  Wallet,
  Clock,
  UtensilsCrossed,
  PieChartIcon,
  BarChart3
} from 'lucide-react';
import BuyerLayout from '../../components/layouts/BuyerLayout';

// Color palette for charts
const COLORS = ['#f97316', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1'];

export default function BuyerInsights() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async () => {
    try {
      const response = await buyerInsightsAPI.getInsights(user.id);
      setInsights(response.data);
    } catch (error) {
      toast.error('Failed to load insights');
      console.error('Insights error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <BuyerLayout>
        <div className="space-y-6 pb-mobile-nav">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </BuyerLayout>
    );
  }

  const summary = insights?.summary || { total_orders: 0, total_spent: 0, average_order_value: 0 };
  const spendingByCategory = insights?.spending_by_category || [];
  const ordersByTime = insights?.orders_by_time || [];
  const mostOrderedItems = insights?.most_ordered_items || [];

  return (
    <BuyerLayout>
      <div className="space-y-6 pb-mobile-nav" data-testid="buyer-insights">
        {/* Page Header */}
        <div>
          <h1 className="font-heading text-2xl font-bold">My Insights</h1>
          <p className="text-muted-foreground">Your ordering patterns and spending analysis</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="stats-card">
            <CardContent className="p-4 text-center">
              <ShoppingBag className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold">{summary.total_orders}</p>
              <p className="text-xs text-muted-foreground">Total Orders</p>
            </CardContent>
          </Card>
          <Card className="stats-card">
            <CardContent className="p-4 text-center">
              <Wallet className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">₹{summary.total_spent}</p>
              <p className="text-xs text-muted-foreground">Total Spent</p>
            </CardContent>
          </Card>
          <Card className="stats-card">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">₹{summary.average_order_value}</p>
              <p className="text-xs text-muted-foreground">Avg Order</p>
            </CardContent>
          </Card>
        </div>

        {/* No Data State */}
        {summary.total_orders === 0 ? (
          <Card className="py-16">
            <CardContent className="text-center">
              <UtensilsCrossed className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading text-xl font-semibold mb-2">No Orders Yet</h3>
              <p className="text-muted-foreground">
                Start ordering to see your insights and spending patterns here!
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Chart 1: Spending by Category (Pie Chart) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PieChartIcon className="w-5 h-5 text-primary" />
                  Spending by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {spendingByCategory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No category data available</p>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={spendingByCategory}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="amount"
                        nameKey="category"
                      >
                        {spendingByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`₹${value}`, 'Amount']}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Chart 2: Orders by Time of Day (Bar Chart) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-orange-500" />
                  Orders by Time of Day
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ordersByTime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="time_slot" 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip />
                    <Bar 
                      dataKey="count" 
                      name="Orders"
                      fill="#f97316" 
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Chart 3: Most Ordered Items (Horizontal Bar Chart) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="w-5 h-5 text-green-500" />
                  Most Ordered Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mostOrderedItems.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No item data available</p>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(300, mostOrderedItems.length * 50)}>
                    <BarChart 
                      data={mostOrderedItems} 
                      layout="vertical"
                      margin={{ left: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis 
                        type="category" 
                        dataKey="item_name" 
                        tick={{ fontSize: 12 }}
                        width={100}
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'count') return [value, 'Times Ordered'];
                          if (name === 'total_spent') return [`₹${value}`, 'Total Spent'];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="count" 
                        name="Times Ordered" 
                        fill="#22c55e" 
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </BuyerLayout>
  );
}
